import { Avatar, Box, CardActionArea, Collapse, List, Typography } from "@mui/material"
import React, { useState } from "react"
import { SwipeDirections, useSwipeable } from "react-swipeable"
import { TransitionGroup } from "react-transition-group"
import { IJob, IJobItems, IJobListing } from "../../entities/job/interface"
import { theme } from "../../styles/theme"
import { JobDetail } from "./JobDetail"

interface IJobTileProps {
    job: IJobListing
    index: string
    handleJobStarted: (job: IJob) => Promise<void>
    isGrouped?: boolean
    isLast?: boolean
    swipeThreshold: number
    isCheckAvatar?: boolean
    avatarColor?: string
    onAvatarClick?: () => void
    content?: string
    isSwipe?: boolean
    select?: boolean
    selectColor?: string
    originOnly?: boolean
    destinationOnly?: boolean
    onCheck?: () => void
}

//to be put in job component
export const JobTile: React.FC<IJobTileProps> = ({
    job,
    index,
    handleJobStarted,
    isGrouped,
    isLast,
    swipeThreshold,
    isCheckAvatar,
    avatarColor,
    onAvatarClick,
    content,
    isSwipe,
    select,
    selectColor,
    originOnly = true,
    destinationOnly = true,
    onCheck,
}: IJobTileProps) => {
    const [showDetails, setShowDetails] = useState(false)
    const handleClick = () => {
        setShowDetails(!showDetails)
    }

    const [translateX, setTranslateX] = useState(0)
    const [swipeDirection, setSwipeDirection] = useState<SwipeDirections | null>(null)

    const handlers = useSwipeable({
        onSwipeStart: (eventData) => {
            setSwipeDirection(eventData.dir)
        },
        onSwiping: (eventData) => {
            if (swipeDirection === "Right") {
                setTranslateX(eventData.deltaX)
            }
        },
        onSwipedRight: (eventData) => {
            if (eventData.deltaX > swipeThreshold) {
                handleJobStarted(job)
            }
        },
        onSwiped: () => {
            setTranslateX(0)
            setSwipeDirection(null)
        },
        ...{
            delta: 10, // min distance(px) before a swipe starts. *See Notes*
            preventScrollOnSwipe: false, // prevents scroll during swipe (*See Details*)
            trackTouch: true, // track touch input
            trackMouse: true, // track mouse input
            rotationAngle: 0, // set a rotation angle
            swipeDuration: Infinity, // allowable duration of a swipe (ms). *See Notes*
            touchEventOptions: { passive: true }, // options for touch listeners (*See Details*)
        },
    })

    const bgColour = translateX > swipeThreshold ? "primary.light" : isGrouped ? "primary.dark" : "primary.main"
    const radius = theme.spacing(2)

    const handlerConfig = isGrouped ? {} : isSwipe ? { ...handlers } : {}

    return (
        <Box
            {...handlerConfig}
            key={job._id || index} // Use job.id if available, fallback to index
            sx={{
                width: "100%",
                backgroundColor: bgColour,
                borderTopLeftRadius: isGrouped ? 0 : radius,
                borderTopRightRadius: isGrouped ? 0 : radius,
                borderBottomLeftRadius: isGrouped ? (isLast ? radius : 0) : radius,
                borderBottomRightRadius: isGrouped ? (isLast ? radius : 0) : radius,
                marginTop: isGrouped ? 0 : { xs: 3, sm: 4, md: radius },
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "right",
                transition: isGrouped ? "" : "transform 0.2s ease-out, background-color 0.5s",
                transform: isGrouped ? "" : `translateX(${Math.max(0, translateX)}px)`,
                boxShadow: theme.shadows[5],
                "@keyframes bounce": isSwipe
                    ? {
                          "0%": { left: "0px" },
                          "5%": { left: "5px" },
                          "10%": { left: "0px" },
                          "15%": { left: "3px" },
                          "20%": { left: "0" },
                      }
                    : {},
                animation: isGrouped ? "" : "bounce 5s ease 5s infinite",
            }}
        >
            {/* Counter Avatar */}
            {isCheckAvatar && (
                <Box
                    width="100%"
                    position="absolute"
                    top="-20px"
                    right={select ? 45 : 1}
                    display="flex"
                    justifyContent="end"
                    zIndex={1}
                >
                    <Avatar
                        sx={{
                            boxShadow: 10,
                            backgroundColor: avatarColor,
                            position: "relative",
                            color: avatarColor !== "white" ? "white" : "black",
                        }}
                        onClick={() => {
                            if (onAvatarClick) {
                                onAvatarClick()
                            }
                        }}
                    >
                        {content}
                    </Avatar>
                </Box>
            )}

            {/* Counter Avatar */}
            {select && (
                <Box
                    width="100%"
                    position="absolute"
                    top="-20px"
                    right={1}
                    display="flex"
                    justifyContent="end"
                    zIndex={1}
                >
                    <Avatar
                        sx={{
                            boxShadow: 10,
                            backgroundColor: selectColor,
                            position: "relative",
                            color: selectColor !== "white" ? "white" : "black",
                        }}
                        onClick={() => {
                            if (onCheck) {
                                onCheck()
                            }
                        }}
                    >
                        {"✔"}
                    </Avatar>
                </Box>
            )}

            <Box width={"100%"} display={"flex"} flexDirection={"column"}>
                <CardActionArea onClick={() => handleClick()}>
                    <Box padding={2} paddingBottom={1} paddingLeft={4} marginY={1}>
                        {/* Render the job location details */}
                        {originOnly && (
                            <Box sx={{ marginBottom: 2 }}>
                                <Typography
                                    variant="body2"
                                    color="white"
                                    sx={{ fontWeight: "Medium", fontSize: "1.1rem" }}
                                >
                                    {job.originAddress.address}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {job.billable.account}
                                </Typography>
                            </Box>
                        )}
                        {/* Render the job destination details */}
                        {destinationOnly && (
                            <Box sx={{ marginBottom: 2 }}>
                                <Typography
                                    variant="body2"
                                    color="white"
                                    sx={{ fontWeight: "Medium", fontSize: "1.1rem" }}
                                >
                                    {job.destinationAddress.address}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {job.destination.account}
                                </Typography>
                            </Box>
                        )}

                        {/* Render job item details like pallets */}
                        <Box>
                            {job.items?.map((item: IJobItems, palletIndex: number) => {
                                const quantity = item.quantityUnloaded ?? item.quantityLoaded ?? item.quantity
                                return (
                                    <Typography key={palletIndex} variant="body2" color="white">
                                        {quantity} x {item.name}
                                    </Typography>
                                )
                            })}
                        </Box>
                    </Box>
                </CardActionArea>
                {/* One might think you could simplify this, but for some reason we need an array here */}
                {/* Unsure if the Accordian method used by GroupJobTile is cleaner. It needs a lot of CSS overrides */}
                <List>
                    <TransitionGroup>
                        {(showDetails ? [job] : []).map((job, index) => (
                            <Collapse key={index}>
                                <JobDetail job={job} />
                            </Collapse>
                        ))}
                    </TransitionGroup>
                </List>
            </Box>
        </Box>
    )
}
